import React, { useEffect, useRef, useState } from 'react';
import { POLICY_CARD, CardSection } from '../../data/policyCard';
import { Bold } from './StudyPage';
import './CardView.css';

/** The participant's task card ("My job and systems"), full or compact. */
const CardView: React.FC<{ compact?: boolean }> = ({ compact }) => (
  <div className={`cal-card ${compact ? 'cal-card-compact' : ''}`}>
    {POLICY_CARD.map(s => (
      <section key={s.title}>
        <h3>{s.title}</h3>
        {s.intro && <p><Bold text={s.intro} /></p>}
        <ul>{s.items.map((it, i) => <li key={i}><Bold text={it} /></li>)}</ul>
      </section>
    ))}
  </div>
);

export default CardView;

/** Right-docked card panel for the task view (advisor §3.3): one Close button. */
export const CardPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <aside className="card-panel" data-testid="card-panel" aria-label="My job and systems">
    <div className="card-panel-head">
      <span>My job and systems</span>
      <button type="button" className="toolbar-button" data-testid="card-close" onClick={onClose}>Close</button>
    </div>
    <div className="card-panel-body"><CardView compact /></div>
  </aside>
);

/* ---------- First reading: one short card per section, Next / Back ----------
 * Presentation only: every sentence of POLICY_CARD is shown, unchanged, in the same order.
 * The " — " separators inside a system or person line become the tile's name / detail / address.
 */
const SHORT: Record<string, string> = {
  'About you': 'About you', 'Your systems': 'Your systems', 'What you do in the job, and what you may do': 'Your job',
  'Your account': 'Your account', 'What you did this week': 'This week', 'People you work with': 'People',
};
const ADDRESS = /university\.edu/;

const Tile: React.FC<{ text: string; wide?: boolean }> = ({ text, wide }) => {
  const parts = text.split(' — ');
  if (parts.length < 2) return <div className="cs-tile"><Bold text={text} /></div>;
  const addr = parts.length > 2 || ADDRESS.test(parts[parts.length - 1]) ? parts[parts.length - 1] : '';
  const detail = parts.slice(1, addr ? -1 : undefined).join(' — ');
  return (
    <div className={`cs-tile cs-named ${wide ? 'cs-wide' : ''}`}>
      <div className="cs-name"><Bold text={parts[0]} /></div>
      {detail && <div className="cs-detail"><Bold text={detail} /></div>}
      {addr && <div className="cs-addr">{addr}</div>}
    </div>
  );
};

const DAY = /^\*\*([^*]+?):\*\*\s*(.*)$/;

const SectionBody: React.FC<{ s: CardSection }> = ({ s }) => {
  if (s.title === 'Your systems' || s.title === 'People you work with') return (
    <div className="cs-grid">{s.items.map((it, i) => <Tile key={i} text={it} wide={it.length > 200} />)}</div>
  );
  if (s.title === 'What you did this week') {
    const days = s.items.filter(it => DAY.test(it));
    const rest = s.items.filter(it => !DAY.test(it));
    return (
      <>
        <ol className="cs-timeline">
          {days.map((it, i) => { const m = it.match(DAY)!; return (
            <li key={i}><span className="cs-day">{m[1]}</span><span className="cs-day-text"><Bold text={m[2]} /></span></li>
          ); })}
        </ol>
        {rest.map((it, i) => <div key={i} className="cs-note"><Bold text={it} /></div>)}
      </>
    );
  }
  return <div className="cs-stack">{s.items.map((it, i) => <div key={i} className="cs-tile"><Bold text={it} /></div>)}</div>;
};

export const CardStepper: React.FC<{ intro: React.ReactNode; doneLabel: string; onDone: () => void }> = ({ intro, doneLabel, onDone }) => {
  const [step, setStep] = useState(0);
  const [seen, setSeen] = useState(0);
  const headRef = useRef<HTMLHeadingElement>(null);
  const first = useRef(true);
  const s = POLICY_CARD[step];
  const last = step === POLICY_CARD.length - 1;
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    window.scrollTo({ top: 0 });
    headRef.current?.focus();
  }, [step]);
  const go = (n: number) => { setStep(n); setSeen(v => Math.max(v, n)); };
  return (
    <div className="cs" data-testid="card-stepper" data-step={step + 1}>
      <div className="cs-progress" aria-label={`Part ${step + 1} of ${POLICY_CARD.length}`}>
        {POLICY_CARD.map((sec, i) => (
          <button key={sec.title} type="button" className={`cs-seg ${i === step ? 'current' : ''} ${i <= seen ? 'done' : ''}`}
            disabled={i > seen} onClick={() => go(i)} aria-label={SHORT[sec.title] || sec.title}>
            <span className="cs-seg-bar" />
            <span className="cs-seg-label">{SHORT[sec.title] || sec.title}</span>
          </button>
        ))}
      </div>
      {step === 0 && <div className="cs-intro">{intro}</div>}
      <section className="cs-card" key={s.title}>
        <div className="cs-count">Part {step + 1} of {POLICY_CARD.length}</div>
        <h3 className="cs-title" tabIndex={-1} ref={headRef}>{s.title}</h3>
        {s.intro && <p className="cs-callout"><Bold text={s.intro} /></p>}
        <SectionBody s={s} />
      </section>
      {last && <p className="cs-reopen">You can open this again at any time with <b>My job and systems</b> (top right of the inbox).</p>}
      <div className="cs-nav">
        {step > 0
          ? <button type="button" className="cs-back" data-testid="card-back" onClick={() => go(step - 1)}>← Back</button>
          : <span />}
        {last
          ? <button type="button" className="btn btn-primary study-primary" data-testid="card-continue" onClick={onDone}>{doneLabel}</button>
          : <button type="button" className="btn btn-primary study-primary" data-testid="card-next" onClick={() => go(step + 1)}>Next →</button>}
      </div>
    </div>
  );
};

/** "How this works": one spaced card per point; the four decision buttons as a 2x2 grid. */
export const HowThisWorks: React.FC<{ lines: Array<Array<string | { b: string }>> }> = ({ lines }) => {
  const render = (line: Array<string | { b: string }>) =>
    line.map((part, j) => typeof part === 'string' ? <React.Fragment key={j}>{part}</React.Fragment> : <b key={j}>{part.b}</b>);
  // The decision line is "lead: **A** (a), **B** (b), ..." — split it into one tile per button.
  const decisions = (line: Array<string | { b: string }>) => {
    const leadText = typeof line[0] === 'string' ? line[0].replace(/:\s*$/, ':') : '';
    const tiles: Array<{ name: string; what: string }> = [];
    for (let i = 1; i < line.length; i += 2) {
      const name = line[i]; const after = line[i + 1];
      if (typeof name === 'string' || typeof after !== 'string') return null;
      const m = after.match(/\(([^)]*)\)/);
      if (!m) return null;
      tiles.push({ name: name.b, what: m[1] });
    }
    return tiles.length === 4 ? { leadText, tiles } : null;
  };
  return (
    <ol className="how-list" data-testid="how-this-works">
      {lines.map((line, i) => {
        const d = decisions(line);
        return (
          <li key={i} className="how-item">
            <span className="how-num" aria-hidden="true">{i + 1}</span>
            <div className="how-body">
              {d ? (
                <>
                  <div>{d.leadText}</div>
                  <div className="how-grid">
                    {d.tiles.map(t => (
                      <div key={t.name} className={`how-btn how-${t.name.toLowerCase()}`}>
                        <span className="how-btn-name">{t.name}</span>
                        <span className="how-btn-what">{t.what}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : render(line)}
            </div>
          </li>
        );
      })}
    </ol>
  );
};
